import { Fragment, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
// Store
import { useAppDispatch, useAppSelector } from 'app/hooks/useStore';
// Actions
import MixpanelActions from 'app/features/mixpanel/MixpanelActions';
import PageActions from 'app/store/page/page.actions';
// Selectors
import { selectSelectedPageIds } from 'app/store/pagesToolbar/pagesToolbar.selectors';
// Messages
import { pageMessages } from 'app/store/page/page.messages';
// Mui
import { Tooltip } from '@mui/material';
// Icons
import {
  ArrowDropDown as ArrowDropDownIcon
} from '@mui/icons-material';
// Hooks
import useMenu from 'app/hooks/useMenu';
// Components
import Icon from 'app/components/Icon';
import { IconButton } from 'app/components/Mui';
// Dialogs
import ColorPalettePickerDialog from 'app/dialogs/ColorPalettePickerDialog';
// Utils
import usePagesUtilities from '../utils/usePagesUtilities';

const ColorsPickerButton = () => {
  const { t } = useTranslation();
  // Dispatch
  const dispatch = useAppDispatch();
  // State
  const selectedPageIds = useAppSelector(selectSelectedPageIds);

  const [mode, setMode] = useState<'assign' | 'unassign' | null>(null);

  const { getPagesByIds, assignPagesColors, unassignPagesColors } = usePagesUtilities();

  const { Menu, MenuItem, openMenu } = useMenu();

  const handleColorPalettePickerDialogClose = useCallback((colors?:string[]) => {
    if ( !mode || !colors ){
      setMode(null);
      return;
    };
    const pages = getPagesByIds(selectedPageIds);
    const updatedPages = mode === 'assign'
      ? assignPagesColors(pages, colors)
      : unassignPagesColors(pages, colors);

    const message = mode === 'assign'
      ? updatedPages.length > 1 ? pageMessages.updatePagesColor : pageMessages.updatePageColor
      : updatedPages.length > 1 ? pageMessages.removePagesColor : pageMessages.removePageColor
    ;
    dispatch(PageActions.patchPages(updatedPages, message));
    dispatch(MixpanelActions.handleCasePagesBulkActionClick({ action: 'color', numberOfPages: updatedPages.length }));
    setMode(null); // close dialog
    }, [mode, dispatch, selectedPageIds, getPagesByIds, assignPagesColors, unassignPagesColors]
  );

  return (
    <Fragment>
      <Tooltip title={t('labels.colors')}>
        <IconButton name="Pages colors" onClick={openMenu}>
          <Icon icon="color_assign" />
          <ArrowDropDownIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Menu>
        <MenuItem
          name="Assign pages colors"
          onClick={() => setMode('assign')}
        >{t('components.colorsPickerButton.assignColorsButton')}</MenuItem>
        <MenuItem
          name="Unassign pages colors"
          onClick={() => setMode('unassign')}
        >{t('components.colorsPickerButton.unassignColorsButton')}</MenuItem>
      </Menu>
      {mode ? (
        <ColorPalettePickerDialog
          open={!!mode}
          onClose={handleColorPalettePickerDialogClose}
          title={mode === 'assign'
            ? t('components.colorsPickerButton.assignDialogTitle')
            : t('components.colorsPickerButton.unassignDialogTitle')
          }
        />
      ) : null}
    </Fragment>
  )
}

export default ColorsPickerButton;
